import React from 'react';
import { KeyRound, Loader2, Mail, Smartphone, ShieldCheck, ShieldOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import useTwoFactorSettings from '../hooks/useTwoFactorSettings';
import TwoFactorChannelPicker from '../components/TwoFactorChannelPicker';
import TwoFactorSetup from '../components/TwoFactorSetup';
import { AdminError, AdminSkeleton } from './components/PageState';

const AdminSecurity = () => {
  const { user } = useAuth();
  const {
    status, error, pending, code, setCode, busy, load, request, confirm, cancel,
    channel, setChannel,
  } = useTwoFactorSettings();

  if (error && !status) return <AdminError message={error} onRetry={load} />;
  if (!status) return <AdminSkeleton rows={3} label="Güvenlik ayarları yükleniyor" />;

  const enabled = status.enabled;

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--accent-soft)', color: 'var(--accent-primary)' }}>
          <ShieldCheck size={18} />
        </div>
        <div>
          <h1 className="text-xl font-bold">Hesap Güvenliği</h1>
          <p className="text-xs text-slate-400">{user?.username} yönetici hesabının giriş güvenliği</p>
        </div>
      </div>

      <div className="admin-card p-6 max-w-2xl">
        <div className="flex flex-wrap items-start gap-3">
          <div
            className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0"
            style={{
              background: enabled ? 'var(--accent-soft)' : 'var(--admin-surface-hover)',
              color: enabled ? 'var(--accent-primary)' : 'var(--admin-text-secondary)',
            }}
          >
            {enabled ? <ShieldCheck size={20} /> : <ShieldOff size={20} />}
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-semibold">İki Adımlı Doğrulama</div>
            <p className="text-xs text-slate-400 mt-1">
              İsteğe bağlıdır. Açtığınızda yönetim paneline her girişte Google
              Authenticator, SMS veya e-posta ile 6 haneli doğrulama kodu istenir.
            </p>
            <div className="mt-2 text-xs flex items-center gap-1.5 text-slate-400">
              {status.delivery_channel === 'totp'
                ? <KeyRound size={13} style={{ color: 'var(--accent-primary)' }} />
                : status.delivery_channel === 'sms'
                  ? <Smartphone size={13} style={{ color: 'var(--accent-primary)' }} />
                  : <Mail size={13} style={{ color: 'var(--accent-primary)' }} />}
              {status.delivery_target || 'Doğrulama kanalı tanımlı değil'}
            </div>
          </div>
          <span
            className="px-2.5 py-1 rounded text-xs font-semibold"
            style={{
              background: enabled ? 'var(--accent-soft)' : 'var(--admin-surface-hover)',
              color: enabled ? 'var(--accent-primary)' : 'var(--admin-text-secondary)',
            }}
          >
            {enabled ? 'Aktif' : 'Kapalı'}
          </span>
        </div>

        {!status.can_enable && !enabled && (
          <p className="text-xs text-amber-400 mt-4">
            Şu anda kullanılabilir doğrulama yöntemi bulunmuyor.
          </p>
        )}

        {pending ? (
          <form className="mt-5 space-y-3" onSubmit={(event) => { event.preventDefault(); confirm(); }}>
            <TwoFactorSetup key={pending.challenge} pending={pending} admin />
            <input
              aria-label="Doğrulama kodu"
              required
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              minLength={status.code_length || 6}
              maxLength={status.code_length || 6}
              pattern="[0-9]{6}"
              className="admin-input text-center text-xl tracking-[0.4em] font-bold"
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={busy || code.length !== (status.code_length || 6)}
                className="admin-btn-primary flex items-center gap-2 disabled:opacity-50"
              >
                {busy && <Loader2 size={14} className="animate-spin" />}
                {pending.action === 'enable' ? 'Doğrula ve Aç' : 'Doğrula ve Kapat'}
              </button>
              {pending.deliveryChannel !== 'totp' && <button
                type="button"
                disabled={busy}
                onClick={() => request(pending.action)}
                className="admin-btn-secondary disabled:opacity-50"
              >
                Kodu tekrar gönder
              </button>}
              <button type="button" disabled={busy} onClick={cancel} className="admin-btn-secondary disabled:opacity-50">Vazgeç</button>
            </div>
          </form>
        ) : (
          <div className="mt-5">
            {!enabled && (
              <TwoFactorChannelPicker
                channels={status.channels}
                value={channel}
                onChange={setChannel}
                disabled={busy}
                accent="var(--accent-primary)"
              />
            )}
            {enabled && (
              <p className="text-xs text-slate-400 mb-4">
                Kanalı değiştirmek için doğrulamayı kapatıp yeniden açın.
              </p>
            )}
            <button
              type="button"
              disabled={busy || (!enabled && (!status.can_enable || !channel))}
              onClick={() => request(enabled ? 'disable' : 'enable')}
              className={`${enabled ? 'admin-btn-secondary' : 'admin-btn-primary'} mt-4 flex items-center gap-2 disabled:opacity-50`}
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              {enabled ? 'Doğrulamayı Kapat' : channel === 'totp' ? 'Google Authenticator Kurulumunu Başlat' : 'Doğrulamayı Aç'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSecurity;
